import { createRoot } from "react-dom/client";
import App from "./App.tsx";
import "./index.css";
import { logError, logWarning, logInfo } from "@/lib/logger";

/** Structured error shape from runtime / Edge Function (e.g. healthcheck 404). Used to avoid reporting expected errors to Sentry. */
interface StructuredErrorLike {
  error_type?: string;
  has_blank_screen?: boolean;
  filename?: string;
  code?: string;
  status?: number;
  stack?: string;
  response?: { status?: number };
}
function isStructuredErrorLike(v: unknown): v is StructuredErrorLike {
  return typeof v === "object" && v !== null;
}

// Sentry: start init as early as possible so events are never dropped (queue flushes when ready)
if (typeof window !== "undefined") {
  const cfg = window.__RUNTIME_CONFIG__;
  const dsn =
    (typeof cfg?.SENTRY_DSN === "string" && cfg.SENTRY_DSN.trim() ? cfg.SENTRY_DSN : undefined) ||
    (typeof import.meta.env.VITE_SENTRY_DSN === "string" && import.meta.env.VITE_SENTRY_DSN.trim()
      ? import.meta.env.VITE_SENTRY_DSN
      : undefined);
  if (dsn) {
    import("@/lib/sentry").then(({ initSentry }) => {
      initSentry({
        dsn,
        environment:
          import.meta.env.VITE_ENVIRONMENT ||
          (import.meta.env.PROD ? "production" : "preview"),
        release: import.meta.env.VITE_RELEASE_VERSION,
      });
    }).catch((err: unknown) => {
      logError("[Sentry] Init failed", err);
    });
  }
}

// Global error handlers for unhandled promise rejections.
// This prevents blank screens and provides better error reporting
if (typeof window !== "undefined") {
  // Handle unhandled promise rejections
  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason;
    const errorMessage = reason instanceof Error ? reason.message : String(reason);
    
    // Do not report healthcheck 404 to Sentry (function is optional).
    // FAANG-LEVEL: Prevent healthcheck 404 from causing blank screens or error reports
    // Check error message, stringified reason, and all possible 404 indicators
    const reasonString = String(reason);
    const reasonObj = isStructuredErrorLike(reason) ? reason : null;
    
    // Check for structured error format (timestamp, error_type, filename, has_blank_screen).
    const isStructuredError = reasonObj &&
      (reasonObj.error_type === "RUNTIME_ERROR" || reasonObj.has_blank_screen === true);
    
    // Check for Lovable-specific error format.
    const isLovableHealthcheckError =
      isStructuredError &&
      reasonObj.filename?.includes("healthcheck") &&
      (reasonObj.error_type === "RUNTIME_ERROR" || reasonObj.code === "NOT_FOUND");
    
    const isHealthcheck404 =
      isLovableHealthcheckError ||
      ((errorMessage.includes("healthcheck") ||
        reasonString.includes("healthcheck") ||
        reasonString.includes("healthcheck/index.ts") ||
        reasonString.includes("functions/healthcheck") ||
        reasonString.includes("supabase/functions/healthcheck") ||
        reasonObj?.filename?.includes("healthcheck") ||
        reasonObj?.filename?.includes("supabase/functions/healthcheck") ||
        reasonObj?.stack?.includes("healthcheck")) &&
        (errorMessage.includes("404") ||
          errorMessage.includes("NOT_FOUND") ||
          errorMessage.includes("not found") ||
          errorMessage.includes("Requested function was not found") ||
          errorMessage.includes("Edge function returned 404") ||
          errorMessage.includes("Edge Function returned 404") ||
          reasonString.includes("404") ||
          reasonString.includes("NOT_FOUND") ||
          reasonObj?.code === "NOT_FOUND" ||
          reasonObj?.status === 404 ||
          reasonObj?.error_type === "RUNTIME_ERROR" ||
          (isStructuredError && reasonObj?.filename?.includes("healthcheck"))));
    
    if (isHealthcheck404) {
      // Silently ignore healthcheck 404 - it's expected if function not deployed
      // Prevent blank screen by stopping error propagation
      event.preventDefault();
      event.stopPropagation();
      logWarning("[Global] Healthcheck 404 ignored (function is optional)");
      return;
    }

    logError("[Global] Unhandled promise rejection", reason);
    
    // Report to Sentry if available. If Sentry fails, error is logged locally – no silent loss.
    if (import.meta.env.PROD) {
      const errToReport = reason instanceof Error ? reason : new Error(String(reason));
      import("@/lib/sentry")
        .then(({ captureException }) => {
          captureException(errToReport, { tags: { errorType: "unhandled_rejection" } });
        })
        .catch((sentryErr) => {
          logError("[Global] Sentry unavailable; unhandled rejection logged locally:", errToReport);
          logWarning("[Global] Sentry captureException failed:", sentryErr);
        });
    }
    
    // Prevent default browser error logging (we handle it ourselves)
    event.preventDefault();
  });
  
  // Handle global errors
  window.addEventListener("error", (event) => {
    const errorMessage = event.error?.message || event.message || '';
    const filename = event.filename || '';
    const errorStack = event.error?.stack || '';
    
    // Do not report healthcheck 404 to Sentry (function is optional).
    const errorObj = event.error !== undefined && isStructuredErrorLike(event.error) ? event.error : null;
    
    const isStructuredError = errorObj &&
      (errorObj.error_type === "RUNTIME_ERROR" || errorObj.has_blank_screen === true);
    
    const isLovableHealthcheckError =
      isStructuredError &&
      errorObj.filename?.includes("healthcheck") &&
      (errorObj.error_type === "RUNTIME_ERROR" || errorObj.code === "NOT_FOUND");
    
    const isHealthcheck404 =
      isLovableHealthcheckError ||
      ((errorMessage.includes("healthcheck") ||
        filename.includes("healthcheck") ||
        filename.includes("healthcheck/index.ts") ||
        filename.includes("functions/healthcheck") ||
        filename.includes("supabase/functions/healthcheck") ||
        errorStack.includes("healthcheck") ||
        errorObj?.filename?.includes("healthcheck") ||
        errorObj?.filename?.includes("supabase/functions/healthcheck") ||
        errorObj?.stack?.includes("healthcheck")) &&
        (errorMessage.includes("404") ||
          errorMessage.includes("NOT_FOUND") ||
          errorMessage.includes("not found") ||
          errorMessage.includes("Requested function was not found") ||
          errorMessage.includes("Edge function returned 404") ||
          errorMessage.includes("Edge Function returned 404") ||
          errorObj?.code === "NOT_FOUND" ||
          errorObj?.status === 404 ||
          errorObj?.response?.status === 404 ||
          errorObj?.error_type === "RUNTIME_ERROR" ||
          (isStructuredError && errorObj?.filename?.includes("healthcheck"))));
    
    if (isHealthcheck404) {
      event.preventDefault();
      event.stopPropagation();
      logWarning("[Global] Healthcheck 404 ignored (function is optional)");
      return;
    }

    logError("[Global] Global error", event.error || event.message);
    
    // Report to Sentry if available. If Sentry fails, error is logged locally – no silent loss.
    if (import.meta.env.PROD && event.error) {
      const errToReport = event.error;
      import("@/lib/sentry")
        .then(({ captureException }) => {
          captureException(errToReport, { tags: { errorType: "global_error" } });
        })
        .catch((sentryErr) => {
          logError("[Global] Sentry unavailable; global error logged locally:", errToReport);
          logWarning("[Global] Sentry captureException failed:", sentryErr);
        });
    }
  });
}

// Register Service Worker for PWA support (production only)
if (import.meta.env.PROD && typeof window !== 'undefined' && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/sw.js')
      .then((registration) => {
        logInfo("[ServiceWorker] Registered", registration.scope);
      })
      .catch((error) => {
        logWarning("[ServiceWorker] Registration failed", error);
      });
  });
}

// Error handling for root initialization
try {
  const rootElement = document.getElementById("root");
  
  if (!rootElement) {
    throw new Error("Root element not found. Make sure <div id='root'></div> exists in index.html");
  }

  const root = createRoot(rootElement);
  root.render(<App />);
} catch (error) {
  // Fallback UI if React fails to initialize
  logError("Failed to initialize React app", error);
  
  const rootElement = document.getElementById("root");
  if (rootElement) {
    // Use DOM API instead of innerHTML to avoid CSP issues
    rootElement.textContent = ""; // Clear existing content
    
    const container = document.createElement("div");
    container.style.cssText = "display: flex; flex-direction: column; align-items: center; justify-content: center; min-height: 100vh; padding: 2rem; font-family: system-ui, -apple-system, sans-serif; text-align: center;";
    
    const heading = document.createElement("h1");
    heading.style.cssText = "font-size: 1.5rem; font-weight: 600; margin-bottom: 1rem; color: #dc2626;";
    heading.textContent = "Ett fel uppstod";
    container.appendChild(heading);
    
    const message = document.createElement("p");
    message.style.cssText = "color: #6b7280; margin-bottom: 1.5rem;";
    message.textContent = "Applikationen kunde inte starta. Försök ladda om sidan.";
    container.appendChild(message);
    
    const button = document.createElement("button");
    button.style.cssText = "padding: 0.5rem 1rem; background: #2563eb; color: white; border: none; border-radius: 0.375rem; cursor: pointer; font-size: 0.875rem;";
    button.textContent = "Ladda om sidan";
    button.addEventListener("click", () => window.location.reload());
    container.appendChild(button);
    
    const details = document.createElement("details");
    details.style.cssText = "margin-top: 2rem; text-align: left; max-width: 600px;";
    
    const summary = document.createElement("summary");
    summary.style.cssText = "cursor: pointer; color: #6b7280; font-size: 0.875rem;";
    summary.textContent = "Teknisk information";
    details.appendChild(summary);
    
    const pre = document.createElement("pre");
    pre.style.cssText = "margin-top: 0.5rem; padding: 1rem; background: #f3f4f6; border-radius: 0.375rem; overflow: auto; font-size: 0.75rem; color: #374151;";
    pre.textContent = error instanceof Error 
      ? `${error.message}${error.stack ? '\n' + error.stack : ''}`
      : String(error);
    details.appendChild(pre);
    
    container.appendChild(details);
    rootElement.appendChild(container);
  }
}
